import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";

interface GetTimeOffTypesParams {
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const getTimeOffTypesAPI =
  ({ onSuccess, onError }: GetTimeOffTypesParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/time-off-types`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Fetch Time Off Types error:", error);
      onError?.();
    }
  };

interface CreateTimeOffRequestParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createTimeOffRequestAPI =
  ({ payload, onSuccess, onError }: CreateTimeOffRequestParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/time-off-requests`, payload);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Time off booked successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to book time off");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Time Off Request error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to book time off";
      message.error(errorMessage);
    }
  };

interface GetTimeOffRequestsParams {
  payload?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const getTimeOffRequestsAPI =
  ({ payload, onSuccess, onError }: GetTimeOffRequestsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/time-off-requests`, {
        params: payload,
      });
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Fetch Time Off Requests error:", error);
      onError?.();
    }
  };

interface UpdateTimeOffRequestParams {
  id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateTimeOffRequestAPI =
  ({ id, payload, onSuccess, onError }: UpdateTimeOffRequestParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/time-off-requests/${id}`,
        payload,
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Time off updated successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update time off");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Time Off Request error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to update time off";
      message.error(errorMessage);
    }
  };

interface DeleteTimeOffRequestParams {
  id: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteTimeOffRequestAPI =
  ({ id, onSuccess, onError }: DeleteTimeOffRequestParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/time-off-requests/${id}`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Time off deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete time off");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Time Off Request error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to delete time off";
      message.error(errorMessage);
    }
  };

interface UpdateTimeOffRequestStatusParams {
  id: string;
  status: "pending" | "approved" | "rejected";
  onSuccess: (data: any) => void;
  onError: () => void;
  approvalType?: string;
}

export const updateTimeOffRequestStatusAPI =
  ({
    id,
    status,
    approvalType,
    onSuccess,
    onError,
  }: UpdateTimeOffRequestStatusParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/time-off-requests/${id}/status`,
        { status, approvalType },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || `Time off request ${status} successfully`,
        );
        onSuccess?.(result.data);
      } else {
        message.error(
          result?.message || `Failed to update status to ${status}`,
        );
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Time Off Request Status error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message ||
        `Unable to update status to ${status}`;
      message.error(errorMessage);
    }
  };

interface GetTimeOffConfigParams {
  onSuccess: (data: any) => void;
  onError?: () => void;
}

export const getTimeOffConfigAPI =
  ({ onSuccess, onError }: GetTimeOffConfigParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/time-off-requests/config`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Fetch Time Off Config error:", error);
      onError?.();
    }
  };

interface UpdateTimeOffConfigParams {
  payload: { members: string[] };
  onSuccess: (data: any) => void;
  onError?: () => void;
}

export const updateTimeOffConfigAPI =
  ({ payload, onSuccess, onError }: UpdateTimeOffConfigParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/time-off-requests/config`,
        payload
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Time off configuration saved successfully"
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to save configuration");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Time Off Config error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to save configuration";
      message.error(errorMessage);
    }
  };

interface GetDepartmentOverlappingRequestsParams {
  params: {
    userIds: string;
    startDate: string;
    endDate: string;
    excludeRequestId?: string;
  };
  onSuccess: (data: any[]) => void;
  onError?: () => void;
}

export const getDepartmentOverlappingRequestsAPI =
  ({ params, onSuccess, onError }: GetDepartmentOverlappingRequestsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/time-off-requests/department-overlaps`,
        { params }
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data || []);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Fetch Department Overlapping Requests error:", error);
      onError?.();
    }
  };

interface GetEmployeeDetailsParams {
  id: string;
  year?: number;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getEmployeeDetailsAPI =
  ({ id, year, onSuccess, onError }: GetEmployeeDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/users/${id}/employee-details`, {
        params: year ? { year } : undefined,
      });
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
        return result.data;
      } else {
        onError?.();
        return null;
      }
    } catch (error: any) {
      console.error("Fetch Employee Details error:", error);
      onError?.();
      return null;
    }
  };

